import { useState } from "react";
import { X, Sparkles, Puzzle, Image as ImageIcon, Music, Combine, BookOpenText, ListTree, Scissors, Repeat2, CaseSensitive, Rabbit, Ear, Type, ClipboardList } from "lucide-react";
import { IDS } from "@/lib/testIds";
import LetterSoundMatch from "@/components/games/LetterSoundMatch";
import WordBuilding from "@/components/games/WordBuilding";
import PictureToWord from "@/components/games/PictureToWord";
import BlendingStudio from "@/components/games/BlendingStudio";
import VocabInContext from "@/components/games/VocabInContext";
import ChunkProsody from "@/components/games/ChunkProsody";
import SyllableDivision from "@/components/games/SyllableDivision";
import WordFamily from "@/components/games/WordFamily";
import CaseMatch from "@/components/games/CaseMatch";
import RhymeHunt from "@/components/games/RhymeHunt";
import FirstSound from "@/components/games/FirstSound";
import SentencePuzzle from "@/components/games/SentencePuzzle";
import PhonicsQuiz from "@/components/games/PhonicsQuiz";

const GAMES = [
  {
    key: "match",
    name: "Letter–Sound Match",
    tagline: "Hear a sound, tap the right letter.",
    icon: Music,
    accent: "sky",
    Component: LetterSoundMatch,
  },
  {
    key: "case",
    name: "Case Match",
    tagline: "Match uppercase to lowercase letters.",
    icon: CaseSensitive,
    accent: "pink",
    Component: CaseMatch,
  },
  {
    key: "rhyme",
    name: "Rhyme Hunt",
    tagline: "Find the word that rhymes.",
    icon: Rabbit,
    accent: "lime",
    Component: RhymeHunt,
  },
  {
    key: "firstsound",
    name: "First Sound Sort",
    tagline: "Pick the picture that starts with the sound.",
    icon: Ear,
    accent: "cyan",
    Component: FirstSound,
  },
  {
    key: "build",
    name: "Word Building",
    tagline: "Drag & drop letters to build the word.",
    icon: Puzzle,
    accent: "amber",
    Component: WordBuilding,
  },
  {
    key: "picture",
    name: "Picture to Word",
    tagline: "See a picture, pick the matching word.",
    icon: ImageIcon,
    accent: "rose",
    Component: PictureToWord,
  },
  {
    key: "blend",
    name: "Blending Studio",
    tagline: "Sound-by-sound: c…a…t → cat.",
    icon: Combine,
    accent: "emerald",
    Component: BlendingStudio,
  },
  {
    key: "family",
    name: "Word Families",
    tagline: "Swap the first sound: cat, bat, hat…",
    icon: Repeat2,
    accent: "orange",
    Component: WordFamily,
  },
  {
    key: "syllable",
    name: "Syllable Division",
    tagline: "Tap between letters to split words.",
    icon: Scissors,
    accent: "teal",
    Component: SyllableDivision,
  },
  {
    key: "vocab",
    name: "Vocab in Context",
    tagline: "Guess the meaning from the sentence.",
    icon: BookOpenText,
    accent: "indigo",
    Component: VocabInContext,
  },
  {
    key: "prosody",
    name: "Chunk & Prosody",
    tagline: "Read in phrases — feel the rhythm.",
    icon: ListTree,
    accent: "purple",
    Component: ChunkProsody,
  },
  {
    key: "sentence",
    name: "Sentence Puzzles",
    tagline: "Unscramble the words into a sentence.",
    icon: Type,
    accent: "violet",
    Component: SentencePuzzle,
  },
  {
    key: "quiz",
    name: "Adult Phonics Quiz",
    tagline: "Age 16+ · test your reading knowledge.",
    icon: ClipboardList,
    accent: "fuchsia",
    Component: PhonicsQuiz,
  },
];

const ACCENT = {
  sky: { bg: "bg-sky-500", soft: "bg-sky-50", text: "text-sky-700", border: "hover:border-sky-200" },
  amber: { bg: "bg-amber-500", soft: "bg-amber-50", text: "text-amber-700", border: "hover:border-amber-200" },
  rose: { bg: "bg-rose-500", soft: "bg-rose-50", text: "text-rose-700", border: "hover:border-rose-200" },
  emerald: { bg: "bg-emerald-500", soft: "bg-emerald-50", text: "text-emerald-700", border: "hover:border-emerald-200" },
  orange: { bg: "bg-orange-500", soft: "bg-orange-50", text: "text-orange-700", border: "hover:border-orange-200" },
  teal: { bg: "bg-teal-500", soft: "bg-teal-50", text: "text-teal-700", border: "hover:border-teal-200" },
  indigo: { bg: "bg-indigo-500", soft: "bg-indigo-50", text: "text-indigo-700", border: "hover:border-indigo-200" },
  purple: { bg: "bg-purple-500", soft: "bg-purple-50", text: "text-purple-700", border: "hover:border-purple-200" },
  pink: { bg: "bg-pink-500", soft: "bg-pink-50", text: "text-pink-700", border: "hover:border-pink-200" },
  lime: { bg: "bg-lime-500", soft: "bg-lime-50", text: "text-lime-700", border: "hover:border-lime-200" },
  cyan: { bg: "bg-cyan-500", soft: "bg-cyan-50", text: "text-cyan-700", border: "hover:border-cyan-200" },
  violet: { bg: "bg-violet-500", soft: "bg-violet-50", text: "text-violet-700", border: "hover:border-violet-200" },
  fuchsia: { bg: "bg-fuchsia-500", soft: "bg-fuchsia-50", text: "text-fuchsia-700", border: "hover:border-fuchsia-200" },
};

export default function GamesSection() {
  const [active, setActive] = useState(null);

  const activeGame = GAMES.find((g) => g.key === active);

  return (
    <section
      id="games"
      data-testid={IDS.games.root}
      className="relative py-16 sm:py-24 bg-gradient-to-b from-transparent via-slate-50/50 to-transparent"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-600 mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Phonics Mini-Games
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
            Play. Read. <span className="text-amber-500">Repeat.</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Thirteen phonics games covering matching, case, rhyming, first
            sounds, building, blending, syllables, word families, vocabulary,
            phrasing, sentence puzzles, and an adult quiz &mdash; free forever,
            no sign-up.
          </p>
        </div>

        {!activeGame && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {GAMES.map((g) => {
              const a = ACCENT[g.accent];
              const Icon = g.icon;
              return (
                <button
                  key={g.key}
                  data-testid={IDS.games.launcher(g.key)}
                  onClick={() => setActive(g.key)}
                  className={`text-left bg-white rounded-3xl p-8 border-2 border-slate-100 ${a.border} transition-all shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 duration-300 flex flex-col group`}
                >
                  <div className={`w-14 h-14 rounded-2xl ${a.bg} flex items-center justify-center shadow-md group-hover:rotate-6 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-slate-900 mt-5">
                    {g.name}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{g.tagline}</p>
                  <span className={`mt-5 inline-flex items-center gap-1 ${a.soft} ${a.text} px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest self-start`}>
                    Play now →
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {activeGame && (
          <div className="bg-white rounded-[2rem] p-6 sm:p-10 border-2 border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] relative">
            <button
              data-testid={IDS.games.close}
              onClick={() => setActive(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"
              aria-label="Close game"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="mb-6">
              <h3 className="font-display text-3xl font-semibold text-slate-900">
                {activeGame.name}
              </h3>
              <p className="text-slate-600 mt-1">{activeGame.tagline}</p>
            </div>
            <activeGame.Component />
          </div>
        )}
      </div>
    </section>
  );
}
